import React from 'react';

export interface LovedayTextSegment {
  text: string;
  href?: string;
  variant?: 'default' | 'link' | 'strong' | 'badge';
}

export interface LovedayTextParagraph {
  segments: LovedayTextSegment[];
}

const segmentClassNames: Record<
  NonNullable<LovedayTextSegment['variant']>,
  string
> = {
  default: '',
  link: 'text-text-link-teal underline underline-offset-2 decoration-text-link-teal',
  strong: 'font-bold text-heading-dark',
  badge:
    'inline-flex items-center rounded-md border border-border-info-bright px-2 py-0.5 text-[14px] font-medium leading-p-lg text-brand-purple',
};

interface LovedayStructuredTextProps {
  paragraphs?: LovedayTextParagraph[];
  paragraphClassName?: string;
  className?: string;
}

export const renderLovedaySegments = (
  segments: LovedayTextSegment[],
): React.ReactNode =>
  segments.map((segment, index) => {
    const className = segmentClassNames[segment.variant || 'default'];

    if (segment.href) {
      return (
        <a
          key={`${segment.text}-${index}`}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          className={className}
        >
          {segment.text}
        </a>
      );
    }

    if (segment.variant === 'badge') {
      return (
        <span key={`${segment.text}-${index}`} className={className}>
          {segment.text}
        </span>
      );
    }

    return (
      <span key={`${segment.text}-${index}`} className={className}>
        {segment.text}
      </span>
    );
  });

export const LovedayStructuredText = ({
  paragraphs,
  paragraphClassName = '',
  className = '',
}: LovedayStructuredTextProps): React.ReactElement | null => {
  if (!paragraphs || paragraphs.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {paragraphs.map((paragraph, index) => (
        <p key={index} className={paragraphClassName}>
          {renderLovedaySegments(paragraph.segments)}
        </p>
      ))}
    </div>
  );
};
